// ABOUTME: Calendars tab for ChildProfilePage — admin-only CalDAV link management for one child
// ABOUTME: Loads family calendar connections and renders ChildCalendarCheckboxes scoped to this child

import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { calendarAPI } from '../../services/api';
import { CalendarConnectionDto } from '../../types/api';
import ChildCalendarCheckboxes from '../MyFamilyPage/ChildCalendarCheckboxes';
import naluV1Theme from '../../theme/theme.nalu-v1';

interface CalendarsTabProps {
  childId: number;
  familyId: number;
}

export default function CalendarsTab({ childId, familyId }: CalendarsTabProps) {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [connections, setConnections] = useState<CalendarConnectionDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConnections();
  }, [familyId]);

  async function loadConnections() {
    try {
      setLoading(true);
      const data = await calendarAPI.getConnections();
      setConnections(data);
    } catch (error) {
      console.error('Failed to load calendar connections:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (connections.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No calendar connections found. Add calendar sources on the My Family page first.
        </Typography>
      </Box>
    );
  }

  const content = (
    <>
      <Typography variant="h6" gutterBottom>Calendar Sources</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select which calendars to display for this child.
      </Typography>
      <ChildCalendarCheckboxes childId={childId} connections={connections} />
    </>
  );

  // nalu-v1's mockup shows the calendar source list inside a flat card — Paper picks up
  // the theme's global flat-cream-fill + hairline-border styling automatically (issue #1900).
  return isNaluV1Theme ? (
    <Paper data-testid="calendars-tab" sx={{ p: 3, maxWidth: 640 }}>{content}</Paper>
  ) : (
    <Box data-testid="calendars-tab">{content}</Box>
  );
}
